import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import TopContactBar, { useContactBarVisible } from '@/components/TopContactBar';
import { LOGO_DARK } from '@/lib/brandImages';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Pricing', path: '/pricing' },
  { label: 'Who We Are', path: '/about' },
  { label: 'Testimonials', path: '/testimonials' },
  { label: 'Blog', path: '/blog' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const showContactBar = useContactBarVisible();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/80 backdrop-blur-xl border-b border-navy/10' : 'bg-white/60 backdrop-blur-md'}`}>
        {showContactBar && <TopContactBar />}
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center">
            <img src={LOGO_DARK.src} width={LOGO_DARK.width} height={LOGO_DARK.height} alt="Daley Inventories Ltd" className="h-10 md:h-12 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path}
                className={`text-sm font-medium transition-colors ${location.pathname === link.path ? 'text-orange-ink' : 'text-navy hover:text-orange-ink'}`}>
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated && (
              <button onClick={() => logout()} className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-navy border border-navy/20 rounded-full hover:bg-navy/5 transition-colors">
                <LogOut size={15} /> Log Out
              </button>
            )}
            <Link to="/request-quote" className="px-6 py-2.5 bg-orange text-navy text-sm font-semibold rounded-full hover:bg-orange-dark transition-colors">
              Request Quote
            </Link>
            <Link to="/book-now" className="px-6 py-2.5 bg-navy text-white text-sm font-semibold rounded-full hover:bg-navy-light transition-colors">
              Book Now
            </Link>
          </div>
          <button className="lg:hidden text-navy" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen}>
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>
      {mobileOpen && (
        <div className={`fixed inset-0 z-40 bg-white lg:hidden ${showContactBar ? 'pt-[124px]' : 'pt-20'}`}>
          <nav className="flex flex-col items-center gap-6 py-12">
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path} className="text-lg font-medium text-navy">{link.label}</Link>
            ))}
            <div className="flex flex-col gap-3 mt-4 w-64">
              {isAuthenticated && (
                <button onClick={() => logout()} className="flex items-center justify-center gap-2 px-8 py-3 text-sm font-medium text-navy border border-navy/20 rounded-full">
                  <LogOut size={16} /> Log Out
                </button>
              )}
              <Link to="/request-quote" className="px-8 py-3 bg-orange text-navy text-sm font-semibold rounded-full text-center">Request Quote</Link>
              <Link to="/book-now" className="px-8 py-3 bg-navy text-white text-sm font-semibold rounded-full text-center">Book Now</Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}